import React, {
    ReactElement,
    ReactNode,
} from "react";
import useTableData, { ContainsId, SerializeType } from "../base";

export interface TableControlsFilterProps<T extends ContainsId, TValue> {
    onFilter?: (item: T, filter: TValue) => boolean | undefined | null;
    defaultValue?: TValue;
    serialize?: (value: TValue) => SerializeType;
    deserialize?: (value: SerializeType) => TValue | undefined;
    filterKey?: string;
    isEqual?: (left: TValue | undefined, right: TValue | undefined) => boolean | undefined;
    children: ((
        filter: TValue,
        setValue: ((child: TValue) => void)) => ReactNode
    ) | ReactNode;
}

function TableControlsFilter<T extends ContainsId, TValue>(args: TableControlsFilterProps<T, TValue>): ReactNode {
    const {
        children,
        onFilter,
        defaultValue,
        serialize,
        deserialize,
        filterKey,
        isEqual,
    } = args;

    const data = useTableData<T>();

    let render;
    if (children instanceof Function) {
        render = children;
    } else if (children != null) {
        render = () => children;
    }

    data.useFilter<TValue>({
        filter: onFilter,
        dependencies: [],
        defaultValue,
        render,
        serialize,
        deserialize,
        filterKey,
        isEqual,
    });

    return null;
}

export interface DateRange {
    startDate?: number;
    endDate?: number;
}

const Serializers = {
    serializeArray: <T extends string>(value: T[]) => (Array.isArray(value) ? value : []),
    deserializeArray: <T extends string>(keys: SerializeType): T[] | undefined => {
        if (Array.isArray(keys)) {
            return keys as T[];
        }
        if (!keys || typeof keys === "object") {
            return undefined;
        }

        return [keys] as T[];
    },
    serializeSet: (value: Set<string>) => Array.from(value),
    deserializeSet: (keys: SerializeType) => {
        if (Array.isArray(keys)) {
            return new Set(keys);
        }
        if (!keys || typeof keys === "object") {
            return undefined;
        }

        return new Set([keys]);
    },

    serializeString: <T extends string | number | boolean>(value: T) => value?.toString(),
    deserializeString: <T extends string | number | boolean>(keys: SerializeType) => (
        typeof keys === "string" ? keys as T : undefined
    ),

    serializeDateRange: (value: DateRange) => value as Record<string, string>,
    deserializeDateRange: (keys: SerializeType) => (typeof keys === "object" && !Array.isArray(keys)
        ? {
            startDate: keys.startDate ? parseInt(keys.startDate, 10) : undefined,
            endDate: keys.endDate ? parseInt(keys.endDate, 10) : undefined,
        }
        : undefined),
};

export type Type<T extends ContainsId> = (<TValue>(
    props: TableControlsFilterProps<T, TValue> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => ReactElement) & {
    Serializers: typeof Serializers
}

TableControlsFilter.Serializers = Serializers;

export default TableControlsFilter as Type<ContainsId>;
